// SELETOR DE ETIQUETAS — a grade de chips que marca/desmarca etiqueta de um contato ([DA-694]).
//
// Nasceu na aba "Contatos do dia" (Gestao de Vendas) e virou peca compartilhada no mesmo dia, a
// pedido dele: *"Tem como atualizar agora o painel de contatos?"*. ⛔ **Uma copia so'** — as duas
// telas marcam a MESMA etiqueta no MESMO contato, e regra duplicada em dois arquivos e' o buraco
// da [DA-659]: a copia esquecida e' sempre a que ninguem abre.
//
// O que a forma resolve, medido no seletor antigo antes de trocar: (a) cada etiqueta gastava tres
// elementos (caixa + selo + nome), com o nome repetindo o selo palavra por palavra em 8 das 15;
// (b) o fluxo quebrava onde coubesse e nenhuma coluna alinhava; (c) 9 das 15 etiquetas tem a MESMA
// cor — o selo colorido PARECIA separar as opcoes e nao separava. Por isso quem identifica aqui e'
// o TEXTO, e a cor entra so' como amarra visual com o selo que a tela mostra em outro lugar.
import { Check, Lock } from 'lucide-react';
import { corDoTexto } from './BadgeEtiqueta';
import type { Etiqueta } from '../lib/etiquetas-context';

/**
 * O nome da etiqueta so' aparece no chip quando ACRESCENTA alguma coisa ao apelido.
 *
 * `EL` → `lista_el` fica (o apelido sozinho nao diz de que lista se trata); `AQUARIO` → `aquario`
 * some. ⚠️ A tolerancia ao plural existe por caso real: `OUTRO`/`outros` e `NAO CLIENTE`/`nao
 * clientes` sao a mesma palavra, e sem ela o chip repetiria o rotulo so' por causa de um `s`.
 */
export function nomeAcrescenta(apelido: string, nome: string): boolean {
  const achatar = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/s$/, '');
  return achatar(apelido) !== achatar(nome);
}

interface Props {
  /** So' as `visiveis` do contexto — a etiqueta de vendedora nao entra no seletor ([DA-687]). */
  etiquetas: Etiqueta[];
  /** Ids marcados HOJE no contato. */
  marcadas: number[];
  onAlternar: (id: number) => void;
  /** Trava os chips: gravacao em curso, ou conjunto atual ainda desconhecido (ver PainelContato). */
  desabilitado?: boolean;
  /** Largura minima da coluna, em PIXEL — a raiz do app e' 14px e o `w-*` do Tailwind e' rem. */
  colunaMin?: number;
  /**
   * ⛔ **So' ligue onde a grade tem 2+ colunas.** O chip do `nao_cliente` carrega o selo amarelo
   * do aviso, que rouba largura da celula e trunca o rotulo (`NAO CLIENTE na...`); numa grade
   * larga a saida e' ele ocupar duas colunas. **Numa grade de coluna UNICA isso estouraria a
   * lateral** (o `span 2` cria uma coluna implicita), entao ali o aviso quebra pra segunda linha
   * do proprio chip — que e' o que o painel lateral de 202px uteis faz.
   */
  avisoEmDuasColunas?: boolean;
  /**
   * Ids que ja estavam GRAVADOS e este usuario nao pode retirar ([DA-722], regra dele: *"o sistema
   * nao deixa desmarcar as que ja foram gravadas — se a mudanca for legitima, o Admin realiza"*).
   *
   * ⚠️ E' so' sobre o que veio do BANCO. O chip que a pessoa acabou de marcar nesta tela continua
   * destravavel — travar tambem esse faria um clique errado de um segundo virar permanente, que e'
   * o oposto do que a regra quer evitar (ela protege o conjunto ja conferido, nao o gesto em curso).
   *
   * ⛔ Trava com `aria-disabled` + clique inerte, **nunca `disabled`**: em elemento desabilitado o
   * navegador nao mostra o `title`, e o chip ficaria mudo — botao que nao responde e nao diz por
   * que le como defeito, e a pessoa clica de novo achando que a tela travou.
   */
  travadas?: number[];
  /**
   * Uma etiqueta por linha ([DA-722], pedido dele em 12/09/2026: *"as Etiquetas devem ser exibidas
   * em uma coluna em ordem alfabetica"*).
   *
   * ⚠️ O motivo e' de LEITURA, nao de gosto: em grade, a ordem alfabetica se le em zigue-zague
   * (AQUARIO, AR CONDICIONADO, CONSTRUTOR na 1a linha; CONSUMIDOR na 2a), e quem procura um nome
   * precisa varrer as duas direcoes. Em coluna unica a ordem fica obvia e o dedo desce reto.
   */
  umaColuna?: boolean;
}

export function SeletorEtiquetas({ etiquetas, marcadas, onAlternar, desabilitado, colunaMin = 215, avisoEmDuasColunas, travadas, umaColuna }: Props) {
  if (etiquetas.length === 0) {
    return (
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Nenhuma etiqueta cadastrada. Elas se criam em Configuracoes → Chat Atendimento.
      </span>
    );
  }

  return (
    <div className="grid" style={{ gridTemplateColumns: umaColuna ? '1fr' : `repeat(auto-fill, minmax(${colunaMin}px, 1fr))`, gap: '6px 8px', alignItems: 'start' }}>
      {etiquetas.map((e) => {
        const marcada = marcadas.includes(e.id);
        const travada = !!travadas?.includes(e.id);
        const cor = e.cor || '#e2e8f0';
        return (
          <button
            key={e.id}
            type="button"
            aria-pressed={marcada}
            aria-disabled={travada || undefined}
            title={travada ? 'Ja gravada neste contato. So um administrador pode retirar.' : undefined}
            onClick={() => { if (!travada) onAlternar(e.id); }}
            disabled={desabilitado}
            className="flex items-center gap-1.5 rounded-2xl text-[11px] font-bold text-left min-w-0 flex-wrap"
            style={{
              // ⛔ Em coluna unica o `span 2` criaria uma coluna IMPLICITA e estouraria a lateral —
              // a mesma armadilha que a prop `avisoEmDuasColunas` ja descreve.
              gridColumn: e.nao_cliente && avisoEmDuasColunas && !umaColuna ? 'span 2' : undefined,
              padding: '5px 9px',
              // ⛔ Marcada: a cor da PROPRIA etiqueta, com o texto saindo do mesmo `corDoTexto` do
              // selo — o chip tem que parecer o selo que ele representa, que aparece na mesma tela.
              // Desmarcada: o trio CASADO do input, que o tema move junto ([DA-678] — nunca duas
              // vars de familias diferentes, que e' o 2,7:1 que ele nao conseguiu ler).
              backgroundColor: marcada ? cor : 'var(--input-bg)',
              color: marcada ? corDoTexto(cor) : 'var(--input-fg)',
              border: `1.5px solid ${marcada ? 'rgba(0,0,0,.45)' : 'var(--input-border)'}`,
              boxShadow: marcada ? '0 1px 3px rgba(0,0,0,.25)' : undefined,
              opacity: desabilitado ? 0.6 : 1,
              cursor: travada ? 'not-allowed' : undefined,
            }}
          >
            {/* ⛔ O "marcada" NAO pode depender de enxergar cor ([[user_daltonismo]]): quem carrega
                o estado e' o preenchimento + o tique. A bolinha da cor no estado apagado e' o que
                amarra o chip ao selo que a tela mostra em outro lugar.
                ⛔ O travado se distingue por FORMA (cadeado no lugar do tique), nunca por cor: ele
                E' marcado, e a diferenca esta em quem pode mexer ([DA-722]). */}
            <span
              className="flex-none rounded-full flex items-center justify-center"
              style={{ width: 14, height: 14, backgroundColor: marcada ? 'rgba(0,0,0,.25)' : cor, color: '#fff' }}
            >
              {marcada && (travada ? <Lock size={9} strokeWidth={3} /> : <Check size={10} strokeWidth={3.5} />)}
            </span>
            {/* ⛔ **QUEBRA, nunca reticencia.** Medido no painel lateral (186px uteis, uma coluna):
                com `truncate` o `FORNECEDOR MERC fornecedor` saia cortado E o chip ficava com 48px
                de altura mesmo assim — o pior dos dois mundos. Na grade larga nada quebra (todos
                cabem em 215px), entao permitir a quebra nao muda nada la e resolve aqui. */}
            <span style={{ flex: '1 1 auto', minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              {e.apelido || e.nome}
              {nomeAcrescenta(e.apelido || e.nome, e.nome) && (
                <span className="font-semibold" style={{ opacity: 0.65 }}> {e.nome}</span>
              )}
            </span>
            {/* ⚠️ O aviso vem ANTES do clique: marcar esta etiqueta tira o contato da Gestao de
                Vendas inteira ([DA-693]), e no chat isso nao produz sinal nenhum na tela — aqui a
                consequencia e' MAIS invisivel que na aba de contatos, onde ao menos a linha some.
                ⛔ Cores FIXAS (`#fde68a` sobre preto), nunca var de tema ([DA-678]). */}
            {e.nao_cliente && (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded font-bold flex-none"
                style={{ backgroundColor: '#fde68a', color: '#000' }}
                title="Quem carrega esta etiqueta some da Gestao de Vendas e das estatisticas. O chat nao muda."
              >
                some da Gestao
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
